import { ReadingsDataRawResponse } from "@inrange/building-manager-api-client";
import React, { useMemo, useState } from "react";
import { Col, Row, Table } from "react-bootstrap";
import Select from "react-select";
import { Formatters } from "./types";

const MAX_TABLE_ROWS = 5000;

const RecentDataTableRaw = React.memo(
  ({
    data,
    selectedEsysUuid,
    startDate,
    endDate,
    formatters,
  }: {
    data: ReadingsDataRawResponse;
    selectedEsysUuid: string | undefined;
    startDate: string;
    endDate: string;
    formatters: Formatters;
  }) => {
    const [filterHardwareType, setFilterHardwareType] = useState<string[]>([]);
    const [filterSerialNumber, setFilterSerialNumber] = useState<string[]>([]);
    const [filterReadingName, setFilterReadingName] = useState<string[]>([]);

    // Get recent data (up to 7 days) for detailed table view
    const recentDataTable = useMemo(() => {
      const rawData = data;

      // Pre-calculate timestamp bounds for efficient filtering
      const { startMs, endMs } =
        startDate && endDate
          ? formatters.dateRangeToTimestamps(startDate, endDate)
          : { startMs: 0, endMs: Infinity };

      // Collect all data points from all series
      const allPoints: Array<{
        timestamp: string;
        hardwareType: string;
        serialNumber: string;
        readingName: string;
        reading: number;
        source: string;
        ingestionTimestamp: string;
      }> = [];

      rawData.series.forEach((s) => {
        // Filter by esysUuid
        if (selectedEsysUuid && s.esysUuid !== selectedEsysUuid) return;

        s.data.x.forEach((timestamp, index) => {
          // Filter by date range if set
          if (startDate && endDate) {
            const pointMs = new Date(timestamp).getTime();
            if (pointMs < startMs || pointMs > endMs) return;
          }

          allPoints.push({
            timestamp,
            hardwareType: s.hardwareType,
            serialNumber: s.serialNumber,
            readingName: s.readingName,
            reading: s.data.reading[index],
            source: s.data.source[index],
            ingestionTimestamp: s.data.ingestionTimestamp[index],
          });
        });
      });

      if (allPoints.length === 0) return [];

      // Sort by timestamp descending to find most recent
      const sortedByDate = allPoints.sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      const mostRecentDate = new Date(sortedByDate[0].timestamp);

      // Calculate 7 days before the most recent date
      const sevenDaysAgo = new Date(mostRecentDate);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6); // 7 days inclusive

      // Filter to only include last 7 days and sort oldest to newest
      return sortedByDate
        .filter((point) => {
          const pointDate = new Date(point.timestamp);
          return pointDate >= sevenDaysAgo;
        })
        .sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
    }, [data, selectedEsysUuid, startDate, endDate, formatters]);

    // Extract unique values for filter dropdowns
    const uniqueHardwareTypes = useMemo(() => {
      const types = new Set(recentDataTable.map((point) => point.hardwareType));
      return Array.from(types).sort();
    }, [recentDataTable]);

    const uniqueSerialNumbers = useMemo(() => {
      const serials = new Set(
        recentDataTable.map((point) => point.serialNumber)
      );
      return Array.from(serials).sort();
    }, [recentDataTable]);

    const uniqueReadingNames = useMemo(() => {
      const names = new Set(recentDataTable.map((point) => point.readingName));
      return Array.from(names).sort();
    }, [recentDataTable]);

    // Apply filters to table data
    const filteredDataTable = useMemo(() => {
      return recentDataTable.filter((point) => {
        if (
          filterHardwareType.length > 0 &&
          !filterHardwareType.includes(point.hardwareType)
        )
          return false;
        if (
          filterSerialNumber.length > 0 &&
          !filterSerialNumber.includes(point.serialNumber)
        )
          return false;
        if (
          filterReadingName.length > 0 &&
          !filterReadingName.includes(point.readingName)
        )
          return false;
        return true;
      });
    }, [
      recentDataTable,
      filterHardwareType,
      filterSerialNumber,
      filterReadingName,
    ]);

    // Limit displayed rows for performance
    const displayData = useMemo(() => {
      return filteredDataTable.slice(0, MAX_TABLE_ROWS);
    }, [filteredDataTable]);

    const isTruncated = filteredDataTable.length > MAX_TABLE_ROWS;

    return (
      <>
        {recentDataTable.length > 0 && (
          <Row className="mt-4">
            <Col>
              <h5>Recent Data (Last 7 Days)</h5>
              <p className="text-muted">
                Showing {displayData.length.toLocaleString()}
                {isTruncated &&
                  ` (limited from ${filteredDataTable.length.toLocaleString()})`}{" "}
                of {recentDataTable.length.toLocaleString()} data point
                {recentDataTable.length !== 1 ? "s" : ""} from the most recent 7
                days in the selected range
              </p>
              <Row className="mb-3">
                <Col md={4}>
                  <div className="mb-3">
                    <label className="form-label">Hardware Type</label>
                    <Select
                      isMulti
                      options={uniqueHardwareTypes.map((type) => ({
                        value: type,
                        label: type,
                      }))}
                      value={filterHardwareType.map((type) => ({
                        value: type,
                        label: type,
                      }))}
                      onChange={(selected) =>
                        setFilterHardwareType(
                          selected ? selected.map((s) => s.value) : []
                        )
                      }
                      placeholder="All"
                      isClearable
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <label className="form-label">Serial Number</label>
                    <Select
                      isMulti
                      options={uniqueSerialNumbers.map((serial) => ({
                        value: serial,
                        label: serial,
                      }))}
                      value={filterSerialNumber.map((serial) => ({
                        value: serial,
                        label: serial,
                      }))}
                      onChange={(selected) =>
                        setFilterSerialNumber(
                          selected ? selected.map((s) => s.value) : []
                        )
                      }
                      placeholder="All"
                      isClearable
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <label className="form-label">Reading Name</label>
                    <Select
                      isMulti
                      options={uniqueReadingNames.map((name) => ({
                        value: name,
                        label: name,
                      }))}
                      value={filterReadingName.map((name) => ({
                        value: name,
                        label: name,
                      }))}
                      onChange={(selected) =>
                        setFilterReadingName(
                          selected ? selected.map((s) => s.value) : []
                        )
                      }
                      placeholder="All"
                      isClearable
                    />
                  </div>
                </Col>
              </Row>
              <div>
                <Table striped bordered hover size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Hardware Type</th>
                      <th>Serial Number</th>
                      <th>Reading Name</th>
                      <th>Reading</th>
                      <th>Source</th>
                      <th>Ingested</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayData.map((point: any, index: number) => (
                      <tr key={index}>
                        <td>{formatters.toLocaleString(point.timestamp)}</td>
                        <td>{point.hardwareType}</td>
                        <td>
                          <code>{point.serialNumber}</code>
                        </td>
                        <td>{point.readingName}</td>
                        <td>{point.reading.toFixed(3)}</td>
                        <td>{point.source && <code>{point.source}</code>}</td>
                        <td>
                          {formatters.toLocaleString(point.ingestionTimestamp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>
        )}
      </>
    );
  }
);

RecentDataTableRaw.displayName = "RecentDataTableRaw";

export default RecentDataTableRaw;
